import { useState, useEffect } from "react"
import { useHistory, useParams, useLocation } from "react-router-dom"
import { useQuery, useMutation, useQueryClient } from "react-query"
import { useTranslation } from "react-i18next"
import { Button, Notification, DialogView, PageView, PageTopBar } from "../../../components"
import { GenerateTemplateLabel } from "./components/GenerateTemplateLabel"
import {
    createContainerTemplate,
    updateContainerTemplate,
    fetchContainerTemplates,
    fetchProduct,
} from "../../../services/containerTemplateServices"
import { CONFIGURATIONS_CONTAINER_TEMPLATES } from "../../../navigation/constants"
import { ContainerDiagram } from "./components/ContainerDiagram"

export const TemplateEditorContainer = () => {
    const { i18n } = useTranslation()
    const history = useHistory()
    const { warehouse_id, template_id } = useParams()
    const location = useLocation()
    const queryClient = useQueryClient()

    const isEditMode = !!template_id && template_id !== 'new'
    // Prefer location.state (no refetch when navigating from the list) but
    // fall back to the URL query string so refreshes / direct links work.
    const queryStoreId = new URLSearchParams(location.search).get('store_id')
    const storeId = location.state?.storeId || (queryStoreId ? parseInt(queryStoreId, 10) : null)
    const stateTemplate = location.state?.template
    const [hydratedTemplate, setHydratedTemplate] = useState(null)
    const existingTemplate = stateTemplate || hydratedTemplate

    // Form state
    const [formData, setFormData] = useState({
        grouping_code: '',
        name: '',
        container_type: '',
        active: true,
    })
    const [items, setItems] = useState([])
    const [editingIndex, setEditingIndex] = useState(null)
    const [errors, setErrors] = useState({})
    const [notification, setNotification] = useState({ show: false, type: '', title: '' })
    const [showLabelDialog, setShowLabelDialog] = useState(false)

    // Load existing template data and resolve product names
    useEffect(() => {
        let isMounted = true
        if (isEditMode && existingTemplate) {
            setFormData({
                grouping_code: existingTemplate.grouping_code || '',
                name: existingTemplate.name || '',
                container_type: existingTemplate.container_type || '',
                active: existingTemplate.active !== false,
            })
            const loadedItems = (existingTemplate.container_content_template_items || []).map((item) => ({
                _clientId: item.id != null ? `server-${item.id}` : `client-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                product_id: item.product_id,
                product: item.product || null,
                child_template_id: item.child_template_id,
                child_template: item.child_template || null,
                quantity: item.quantity,
                units_per_container: item.units_per_container,
                lot_id: item.lot_id,
                type: item.child_template_id ? 'sub_container' : 'product',
                container_type: item.container_type,
            }))
            setItems(loadedItems)

            // Resolve product details for items that only have product_id
            const productItemsToResolve = loadedItems.filter(item => item.product_id && !item.product?.name)
            if (productItemsToResolve.length > 0) {
                const uniqueProductIds = [...new Set(productItemsToResolve.map(item => item.product_id))]
                Promise.all(uniqueProductIds.map(id => fetchProduct(id).catch(() => null)))
                    .then(products => {
                        if (!isMounted) return
                        const productMap = {}
                        products.forEach((product) => {
                            if (product) {
                                const p = product.product || product
                                productMap[p.id] = p
                            }
                        })
                        setItems(prev => prev.map(item => {
                            if (item.product_id && productMap[item.product_id]) {
                                return { ...item, product: productMap[item.product_id] }
                            }
                            return item
                        }))
                    })
            }
        }
        return () => { isMounted = false }
    }, [isEditMode, existingTemplate])

    // Fetch available templates for sub-container selection (same store, exclude self)
    const { data: allTemplatesData } = useQuery(
        ['container-content-templates', storeId],
        () => fetchContainerTemplates(storeId),
        {
            enabled: !!storeId,
            refetchOnWindowFocus: false,
        }
    )

    const allTemplates = allTemplatesData?.container_content_templates || allTemplatesData || []
    const allTemplatesList = Array.isArray(allTemplates) ? allTemplates : []
    const availableTemplates = allTemplatesList.filter(
        t => !isEditMode || t.id !== parseInt(template_id)
    )

    // If we landed here without router state (refresh / direct URL) hydrate the
    // template from the list response so the form can populate.
    useEffect(() => {
        if (!isEditMode || stateTemplate || hydratedTemplate) return
        if (allTemplatesList.length === 0) return
        const found = allTemplatesList.find(t => t.id === parseInt(template_id))
        if (found) setHydratedTemplate(found)
    }, [isEditMode, stateTemplate, hydratedTemplate, allTemplatesList, template_id])

    // No storeId means we cannot create or load anything — bounce back to the
    // list with a notification so we never issue requests against /stores/undefined/.
    useEffect(() => {
        if (storeId) return
        showNotification('error', i18n.t('container_templates.notifications.missing_store'))
        const path = CONFIGURATIONS_CONTAINER_TEMPLATES.replace(':warehouse_id', warehouse_id)
        const t = setTimeout(() => history.push(path), 500)
        return () => clearTimeout(t)
    }, [storeId])

    // Mutations
    const createMutation = useMutation(
        (payload) => createContainerTemplate(storeId, payload),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['container-content-templates', storeId])
                showNotification('success', i18n.t('container_templates.notifications.created'))
                navigateBack()
            },
            onError: (error) => {
                showNotification('error', error?.response?.data?.error || i18n.t('container_templates.notifications.error_create'))
            }
        }
    )

    const updateMutation = useMutation(
        (payload) => updateContainerTemplate(storeId, template_id, payload),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['container-content-templates', storeId])
                showNotification('success', i18n.t('container_templates.notifications.updated'))
                navigateBack()
            },
            onError: (error) => {
                showNotification('error', error?.response?.data?.error || i18n.t('container_templates.notifications.error_update'))
            }
        }
    )

    const showNotification = (type, title) => {
        setNotification({ show: true, type, title })
    }

    const navigateBack = () => {
        const path = CONFIGURATIONS_CONTAINER_TEMPLATES.replace(':warehouse_id', warehouse_id)
        setTimeout(() => history.push(path), 500)
    }

    const handleFieldChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }))
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }))
    }

    // Item management — client-side ids keep React reconciliation stable when
    // items are removed/inserted (index keys cause the wrong card to keep edit
    // focus after a splice). Items loaded from the server pick up their id in
    // the load effect below.
    const generateClientId = () => `client-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

    const handleAddItem = (type) => {
        const newItem = type === 'product'
            ? { _clientId: generateClientId(), type: 'product', product_id: null, product: null, quantity: 1, units_per_container: 1, lot_id: null, container_type: 'PRODUCT_CONTAINER' }
            : { _clientId: generateClientId(), type: 'sub_container', child_template_id: null, child_template: null, quantity: 1, container_type: 'GROUP_CONTAINER' }
        setItems(prev => [...prev, newItem])
        setEditingIndex(items.length)
    }

    const handleUpdateItem = (index, updatedItem) => {
        setItems(prev => prev.map((item, i) => i === index ? updatedItem : item))
    }

    const handleRemoveItem = (index) => {
        setItems(prev => prev.filter((_, i) => i !== index))
        setEditingIndex(null)
    }

    const handleStartEdit = (index) => {
        setEditingIndex(index)
    }

    const handleDoneEdit = () => {
        setEditingIndex(null)
    }

    // Validation & Save
    const isItemIncomplete = (it) => {
        if (it.type === 'sub_container' || it.child_template_id) {
            return !it.child_template_id || !it.quantity
        }
        return !it.product_id || !it.quantity || !it.units_per_container
    }

    const validate = () => {
        const newErrors = {}
        if (!formData.grouping_code.trim()) {
            newErrors.grouping_code = i18n.t('container_templates.editor.validation.grouping_code_required')
        }
        if (!formData.container_type) {
            newErrors.container_type = i18n.t('container_templates.editor.validation.container_type_required')
        }
        if (items.length === 0) {
            newErrors.items = i18n.t('container_templates.editor.validation.at_least_one_item')
        } else {
            const invalid = items.findIndex(isItemIncomplete)
            if (invalid !== -1) {
                newErrors.items = i18n.t('container_templates.editor.validation.incomplete_item', { position: invalid + 1 })
                // Surface the offending row so the operator can fix it immediately.
                setEditingIndex(invalid)
            }
        }
        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSave = () => {
        if (!validate()) return

        const payload = {
            ...formData,
            container_content_template_items: items.map(item => {
                const apiItem = {
                    quantity: item.quantity,
                    container_type: item.container_type,
                }
                if (item.type === 'sub_container' || item.child_template_id) {
                    apiItem.child_template_id = item.child_template_id
                } else {
                    apiItem.product_id = item.product_id
                    apiItem.units_per_container = item.units_per_container
                    if (item.lot_id) apiItem.lot_id = item.lot_id
                }
                return apiItem
            }),
        }

        if (isEditMode) {
            updateMutation.mutate(payload)
        } else {
            createMutation.mutate(payload)
        }
    }

    const handleCancel = () => {
        navigateBack()
    }

    const isSaving = createMutation.isLoading || updateMutation.isLoading

    return (
        <PageView
            topMenu={
                <PageTopBar>
                    <div className="text-lg font-semibold">
                        {isEditMode ? i18n.t('container_templates.edit_template') : i18n.t('container_templates.create_template')}
                    </div>
                </PageTopBar>
            }
            childrenFullWidth={true}
            topMenuFullWidth={true}
        >
            <Notification
                show={notification.show}
                setShow={(val) => setNotification(prev => ({ ...prev, show: val }))}
                type={notification.type}
                title={notification.title}
            />

            {/* Container diagram with header fields inside */}
            <ContainerDiagram
                    containerType={formData.container_type || 'GROUP_CONTAINER'}
                    headerContent={
                        <div className="grid grid-cols-4 gap-4 mb-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {i18n.t('container_templates.editor.grouping_code')} *
                                </label>
                                <input
                                    type="text"
                                    className={`w-full border rounded-md px-3 py-2 text-sm bg-white ${errors.grouping_code ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                    placeholder={i18n.t('container_templates.editor.grouping_code_placeholder')}
                                    value={formData.grouping_code}
                                    onChange={(e) => handleFieldChange('grouping_code', e.target.value)}
                                />
                                {errors.grouping_code && <p className="text-xs text-red-600 mt-1">{errors.grouping_code}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {i18n.t('container_templates.editor.name')}
                                </label>
                                <input
                                    type="text"
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder={i18n.t('container_templates.editor.name_placeholder')}
                                    value={formData.name}
                                    onChange={(e) => handleFieldChange('name', e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {i18n.t('container_templates.editor.container_type')} *
                                </label>
                                <select
                                    className={`w-full border rounded-md px-3 py-2 text-sm bg-white ${errors.container_type ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                    value={formData.container_type}
                                    onChange={(e) => handleFieldChange('container_type', e.target.value)}
                                >
                                    <option value="">--</option>
                                    <option value="GROUP_CONTAINER">{i18n.t('container_templates.container_types.GROUP_CONTAINER')}</option>
                                    <option value="PRODUCT_CONTAINER">{i18n.t('container_templates.container_types.PRODUCT_CONTAINER')}</option>
                                </select>
                                {errors.container_type && <p className="text-xs text-red-600 mt-1">{errors.container_type}</p>}
                            </div>
                            <div className="flex items-end">
                                <label className="flex items-center space-x-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        checked={formData.active}
                                        onChange={(e) => handleFieldChange('active', e.target.checked)}
                                    />
                                    <span className="text-sm font-medium text-gray-700">{i18n.t('container_templates.editor.active')}</span>
                                </label>
                            </div>
                        </div>
                    }
                    items={items}
                    editingIndex={editingIndex}
                    storeId={storeId}
                    availableTemplates={availableTemplates}
                    onStartEdit={handleStartEdit}
                    onUpdateItem={handleUpdateItem}
                    onRemoveItem={handleRemoveItem}
                    onDoneEdit={handleDoneEdit}
                    onAddItem={handleAddItem}
                />

            {errors.items && (
                <p className="text-sm text-red-600 mt-2">{errors.items}</p>
            )}

            {/* Action buttons */}
            <div className="flex justify-end space-x-3 mt-6">
                {formData.grouping_code && (
                    <Button onClick={() => setShowLabelDialog(true)}>
                        {i18n.t('container_templates.print_label')}
                    </Button>
                )}
                <Button onClick={handleCancel}>
                    {i18n.t('container_templates.cancel')}
                </Button>
                <Button
                    type="primary"
                    onClick={handleSave}
                    disabled={isSaving}
                >
                    {isSaving ? '...' : i18n.t('container_templates.save_template')}
                </Button>
            </div>

            <DialogView className="w-full" setOpen={setShowLabelDialog} open={showLabelDialog}>
                <GenerateTemplateLabel
                    groupingCode={formData.grouping_code}
                    templateName={formData.name}
                    onCancel={() => setShowLabelDialog(false)}
                    onSuccess={() => setShowLabelDialog(false)}
                />
            </DialogView>
        </PageView>
    )
}
